import { useQuery } from '@tanstack/react-query';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';

import CarouselBox from 'components/CarouselBox';
import Card from 'components/Timeline/OfferCard/V2/OfferGridCard/Card';

import useMyBlackFridayScheduler from 'hooks/useMyBlackFridayScheduler';

import { offerQueryKeys } from 'lib/queryKeys';

import { getFeaturedOffersList } from 'services/offer';
import { getStoreOffersList } from 'services/store';

import { SORT_VALUES } from 'constants/sort';

const OfferCardSkeleton = dynamic(
  () => import('components/Timeline/OfferCard/V2/OfferGridCard/Skeleton'),
  { ssr: false }
);

const SkeletonGroup = dynamic(
  () => import('components/Skeleton/SkeletonGroup'),
  { ssr: false }
);

const ITEMS_LIMIT = 10;

const OffersListCarousel = ({ gaEventCategory }) => {
  const { getCampaignByType } = useMyBlackFridayScheduler();

  const offerCampaign = getCampaignByType('OFFERS');

  const queryFn = offerCampaign
    ? ({ signal }) =>
        getStoreOffersList(
          {
            limit: ITEMS_LIMIT,
            sort: SORT_VALUES.LATEST,
            storeSlug: offerCampaign.storeSlug,
          },
          { signal }
        )
    : ({ signal }) =>
        getFeaturedOffersList(
          {
            limit: ITEMS_LIMIT,
            sort: SORT_VALUES.LATEST,
          },
          { signal }
        );

  const queryKey = offerCampaign
    ? offerQueryKeys.listByStore({
        storeSlug: offerCampaign.storeSlug,
        offersSort: SORT_VALUES.LATEST,
      })
    : offerQueryKeys.listByFeatured();

  const {
    data: offersList,
    isLoading,
    isSuccess,
  } = useQuery({
    queryKey,
    queryFn,
    select: (data) => (offerCampaign ? data?.offers || [] : data || []),
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: true,
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-10">
        <SkeletonGroup items={4} skeleton={OfferCardSkeleton} />
      </div>
    );
  }

  if (!isSuccess || offersList.length === 0) {
    return null;
  }

  return (
    <CarouselBox
      arrowsProps={{
        className: 'hidden lg:flex',
        offsetX: { left: '-left-2', right: '-right-2' },
      }}
      carouselProps={{ className: 'pl-2 pb-3' }}
      className="mb-4"
      dotsProps={{ className: 'xl:hidden' }}
      itemProps={{
        itemSize: 95,
        slidesPerView: 1,
        breakpoints: {
          md: { slidesPerView: 2, itemSize: 90 },
          lg: { slidesPerView: 3, itemSize: 95 },
          xl: { slidesPerView: 4, itemSize: 95 },
        },
      }}
      list={offersList}
      render={(offer, index) => (
        <Card
          key={`${offer.key}-${index}`}
          gaEventCategory={gaEventCategory}
          {...offer}
        />
      )}
      dots
      full
      showArrows
    />
  );
};

OffersListCarousel.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
};

export default OffersListCarousel;
